import React from 'react';
import { TableHead } from './ui/table';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

/**
 * Componente de cabeçalho de tabela ordenável
 * 
 * @param {string} column - Nome da coluna (chave do objeto)
 * @param {string} label - Texto exibido no cabeçalho
 * @param {string} sortColumn - Coluna atualmente ordenada
 * @param {string} sortDirection - Direção da ordenação ('asc' ou 'desc')
 * @param {function} onSort - Callback ao clicar para ordenar
 * @param {string} className - Classes CSS adicionais
 */
export const SortableTableHead = ({ 
  column, 
  label, 
  sortColumn, 
  sortDirection, 
  onSort,
  className = ''
}) => {
  const isActive = sortColumn === column;
  
  const handleClick = () => {
    if (isActive) {
      // Se já está ordenando por esta coluna, inverte a direção
      onSort(column, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Se é uma nova coluna, ordena ascendente por padrão
      onSort(column, 'asc');
    }
  };

  return (
    <TableHead 
      className={`cursor-pointer select-none hover:bg-slate-100 transition-colors ${className}`}
      onClick={handleClick}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className="text-slate-400">
          {isActive ? (
            sortDirection === 'asc' ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )
          ) : (
            <ChevronsUpDown className="w-3 h-3 opacity-50" />
          )}
        </span>
      </div>
    </TableHead>
  );
};

/**
 * Hook para gerenciar estado de ordenação
 * 
 * @param {string} defaultColumn - Coluna padrão para ordenação inicial
 * @param {string} defaultDirection - Direção padrão ('asc' ou 'desc')
 */
export const useSortableTable = (defaultColumn = '', defaultDirection = 'asc') => {
  const [sortColumn, setSortColumn] = React.useState(defaultColumn);
  const [sortDirection, setSortDirection] = React.useState(defaultDirection);

  const handleSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const sortData = (data) => {
    if (!sortColumn || !data || data.length === 0) return data;

    return [...data].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      // Tratar valores nulos/undefined
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // Tratar datas (formato YYYY-MM-DD ou ISO)
      if (typeof aVal === 'string' && aVal.match(/^\d{4}-\d{2}-\d{2}/)) {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      // Tratar números
      else if (!isNaN(Number(aVal)) && !isNaN(Number(bVal))) {
        aVal = Number(aVal);
        bVal = Number(bVal);
      }
      // Tratar strings
      else {
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  return {
    sortColumn,
    sortDirection,
    handleSort,
    sortData
  };
};

export default SortableTableHead;
